import { publicUserSchema } from '@academy/shared';
import { api, ApiError } from './client.js';

/**
 * Endpoints de sesión, ya validados contra el contrato.
 *
 * Cada función devuelve el usuario público o lanza un `ApiError`. La validación
 * con `publicUserSchema` NO es adorno: es lo que hace que, si el backend y el
 * frontend se desincronizan, se vea un error claro en vez de un
 * `Cannot read properties of undefined (reading 'email')` dentro de un
 * componente.
 *
 * @returns {import('@academy/shared').PublicUser}
 */
function usuarioDe(response) {
  const parsed = publicUserSchema.safeParse(response?.data);

  if (!parsed.success) {
    // Se informa del fallo con detalle porque, si se llega aquí, es un bug de
    // este repositorio (los dos lados comparten el mismo schema) y hay que
    // arreglarlo, no convertirlo en algo que "funciona igual".
    console.warn('La respuesta del servidor no encaja con publicUserSchema', parsed.error.issues);

    throw new ApiError({
      code: 'INTERNAL_ERROR',
      status: 0,
      message: 'El servidor ha devuelto un usuario con un formato inesperado',
    });
  }

  return parsed.data;
}

export const authApi = {
  /** @returns {Promise<import('@academy/shared').PublicUser>} */
  register: async (body) => usuarioDe(await api.registro(body)),

  /** @returns {Promise<import('@academy/shared').PublicUser>} */
  login: async (body) => usuarioDe(await api.login(body)),

  /** @returns {Promise<import('@academy/shared').PublicUser | null>} `null` si no hay sesión. */
  me: async () => {
    const response = await api.me();
    // Un 204 aquí significaría "sin sesión", que no es un error de la API sino
    // el estado normal de alguien que no ha iniciado sesión.
    if (response === null) return null;
    return usuarioDe(response);
  },

  logout: () => api.logout(),
  logoutEverywhere: () => api.logoutEverywhere(),

  /**
   * Pide el código de recuperación.
   *
   * La respuesta es la misma exista la cuenta o no: el backend no permite
   * saber si un correo está registrado desde aquí. En desarrollo puede traer
   * `debugCode`, que es el único sitio donde se ve el código sin correo.
   *
   * @returns {Promise<{ message: string, debugCode?: string } | null>}
   */
  solicitarRecuperacion: async (body) => api.datosDe(await api.forgotPassword(body)),

  /** @returns {Promise<{ message: string } | null>} */
  restablecerContrasena: async (body) => api.datosDe(await api.resetPassword(body)),

  /** @returns {Promise<import('@academy/shared').PublicUser>} */
  updateProfile: async (body) => usuarioDe(await api.actualizarPerfil(body)),

  changePassword: (body) => api.cambiarPassword(body),
  deleteAccount: () => api.borrarCuenta(),
};
